"use client";

import { useEffect, useState } from "react";
import { oddsScreenSearchSchema } from "@/lib/odds-screen/live/protocol";
import type { RemoteSearch } from "@/lib/odds-screen/live/search";
import type { LeagueId } from "@/lib/odds-screen/live/types";

const DEBOUNCE_MS = 180;
const MIN_QUERY_LENGTH = 2;
const LIMIT = 8;

type State = { readonly query: string; readonly remote: RemoteSearch | null; readonly pending: boolean };
const IDLE: State = { query: "", remote: null, pending: false };

export function useRemoteSearch(query: string, league: LeagueId, enabled: boolean): { remote: RemoteSearch | null; pending: boolean } {
  const [state, setState] = useState<State>(IDLE);
  const trimmed = query.trim();
  const active = enabled && trimmed.length >= MIN_QUERY_LENGTH;

  useEffect(() => {
    if (!active) { setState(IDLE); return; }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setState((current) => ({ ...current, pending: true }));
      const params = new URLSearchParams({ q: trimmed, league, limit: String(LIMIT) });
      fetch(`/api/odds-screen/search?${params.toString()}`, { signal: controller.signal, cache: "no-store" })
        .then(async (response) => {
          if (!response.ok) return null;
          const parsed = oddsScreenSearchSchema.safeParse(await response.json());
          return parsed.success ? parsed.data : null;
        })
        .catch(() => null)
        .then((remote) => {
          if (controller.signal.aborted) return;
          setState((current) => ({ query: trimmed, remote: remote ?? (current.query === trimmed ? current.remote : null), pending: false }));
        });
    }, DEBOUNCE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [active, trimmed, league]);

  if (!active) return { remote: null, pending: false };
  const current = state.query === trimmed;
  return { remote: current ? state.remote : null, pending: !current || state.pending };
}
